// EmergencyRechargeForm — A1 Margin Call funnel.
//
// One actionable form, two callers:
//   - <EmergencyLockoutGate>  uses this in a full-page non-dismissible overlay
//   - <MarginWarningModal>    uses this in a recurring dismissible centered modal
//
// Data flow:
//   1. On mount, GET /api/admin/me-agent/emergency-recharge — returns
//      marginStatus + balances + shortfall + parent payment config
//      (UPI / QR url / WhatsApp / display note).
//   2. Renders the warning copy + parent's payment instructions + a
//      pre-filled UTR submission form.
//   3. On submit, POST /api/admin/me-agent/recharge with
//      { amount, transaction_id, note }. The existing approval flow
//      routes the resulting agent_recharge_request to the right parent
//      (admin queue OR parent-agent queue, by agent's parent_*_id).
//   4. After a successful submit, calls onSubmittedSuccess() so the
//      caller can dismiss its container.
//
// The form does NOT call refresh() itself — clearing the lockout
// requires the parent to APPROVE the recharge (not just receive the
// submission). The lockout gate has its own "I've already paid /
// refresh status" button for after approval.

import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';

// 2026-05-18 — Identity masking. Backend now returns
// `display_name` (composed from first_name + last_name) instead of
// `username` on this peer-facing endpoint. The username field is
// gone entirely from the response — there's nothing to fall back to.
type ParentInfo = {
  role: 'admin' | 'agent';
  id: number;
  display_name: string;
  uaid: string | null;
  whatsappNumber: string | null;
  supportPhone: string | null;
  upiId: string | null;
  qrImageUrl: string | null;
  displayNote: string | null;
  isActive: boolean | null;
};

type EmergencyData = {
  marginStatus: 'safe' | 'warning' | 'locked';
  walletBalance: number;
  depositBalance: number;
  // Phase 9b v2: three recovery targets.
  //   bareMinimum  = just unlock (re-locks on any new loss)
  //   safeMinimum  = deposit + 50% × |wallet| (real cushion)
  //   recommended  = deposit + 100% × |wallet| (full recovery)
  bareMinimum: number;
  safeMinimum: number;
  recommended: number;
  // Backwards-compat aliases (== bareMinimum / == safeMinimum-when-locked):
  shortfall: number;
  recommendedTopup: number;
  parent: ParentInfo | null;
};

const inrAbs = (n: number) =>
  `₹${Math.abs(n || 0).toLocaleString('en-IN')}`;

export default function EmergencyRechargeForm({
  severity, onSubmittedSuccess,
}: {
  severity: 'warning' | 'locked';
  onSubmittedSuccess?: () => void;
}) {
  const t = useT();
  const { push } = useToast();
  const [data, setData] = useState<EmergencyData | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [txn, setTxn] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ id: number } | null>(null);

  // Load the funnel payload on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<EmergencyData>('/admin/me-agent/emergency-recharge');
        if (cancelled) return;
        setData(r);
        // Phase 10 follow-up (2026-05-12): same default for BOTH
        // warning and locked — safeMinimum (deposit + 50% of negative
        // wallet) is the smallest amount that leaves the agent with
        // a real cushion. bareMinimum would only escape the current
        // crisis state momentarily before re-triggering on any new
        // loss; never default to it.
        const prefill = r.safeMinimum;
        if (prefill > 0) setAmount(String(prefill));
      } catch (e) {
        if (!cancelled) setLoadErr((e as ApiError).message || t('agent.emergencyForm.error.loadFailed'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severity]);

  const onSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErr(null);
    const amt = Math.round(Number(amount));
    if (!Number.isFinite(amt) || amt < 10) {
      setSubmitErr(t('agent.emergencyForm.validation.amount'));
      return;
    }
    if (!txn.trim() || txn.trim().length < 6) {
      setSubmitErr(t('agent.emergencyForm.validation.txn'));
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post<{ id: number }>('/admin/me-agent/recharge', {
        amount: amt,
        transaction_id: txn.trim(),
        note: note.trim() || undefined,
      });
      setSubmitted({ id: r.id });
      push('success', t('agent.emergencyForm.toast.submitted', { id: r.id }));
      onSubmittedSuccess?.();
    } catch (e) {
      setSubmitErr((e as ApiError).message);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, txn, note, push, onSubmittedSuccess]);

  // ── Loading / error states ──────────────────────────────────
  if (loadErr) {
    return (
      <div className="rounded-2xl bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-700/40 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-200">
        {loadErr}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-4 py-6 text-center text-[13px] text-arena-600 dark:text-neon-200/80">
        <Icon name="progress_activity" size={18} className="animate-spin inline-block mr-1.5" />
        Loading recharge details…
      </div>
    );
  }

  // ── Submitted state ─────────────────────────────────────────
  if (submitted) {
    return (
      <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 border border-emerald-200 dark:border-emerald-500/30 px-5 py-5 text-center">
        <Icon name="check_circle" size={36} className="text-emerald-500 mx-auto mb-2" filled />
        <div className="text-[15px] font-bold text-emerald-800 dark:text-emerald-200 mb-1">
          {t('agent.emergencyForm.success.title')}
        </div>
        <p className="text-[13px] text-emerald-700 dark:text-emerald-200/80 leading-relaxed">
          {data.parent?.role === 'admin'
            ? t('agent.emergencyForm.success.bodyAdmin', { id: submitted.id })
            : t('agent.emergencyForm.success.bodyAgent', { id: submitted.id, username: data.parent?.display_name || '—' })}
        </p>
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────
  const parent = data.parent;
  const isLocked = severity === 'locked';
  const tone = isLocked ? 'rose' : 'amber';
  const headline = t(isLocked
    ? 'agent.emergencyForm.banner.locked.title'
    : 'agent.emergencyForm.banner.warning.title');
  const bodyText = t(isLocked
    ? 'agent.emergencyForm.banner.locked.body'
    : 'agent.emergencyForm.banner.warning.body');

  // Format the negative wallet correctly. walletBalance is a signed
  // integer; show with the sign.
  const walletDisplay = data.walletBalance < 0
    ? `-${inrAbs(data.walletBalance)}`
    : inrAbs(data.walletBalance);

  return (
    <div className="space-y-4">
      {/* Warning banner */}
      <div className={`rounded-2xl px-4 py-3.5 ${
        tone === 'rose'
          ? 'bg-rose-50 dark:bg-rose-900/25 border border-rose-200 dark:border-rose-500/30'
          : 'bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-500/30'
      }`}>
        <div className={`text-[14px] font-bold mb-1 ${
          tone === 'rose'
            ? 'text-rose-800 dark:text-rose-200'
            : 'text-amber-800 dark:text-amber-200'
        }`}>
          {headline}
        </div>
        <p className={`text-[12.5px] leading-relaxed ${
          tone === 'rose'
            ? 'text-rose-700/90 dark:text-rose-200/80'
            : 'text-amber-700/90 dark:text-amber-200/80'
        }`}>
          {bodyText}
        </p>
      </div>

      {/* Numbers — your wallet, your deposit (shown plainly). */}
      <div className="rounded-2xl bg-arena-50/40 dark:bg-arena-700/40 border border-arena-200/60 dark:border-arena-600/50 px-4 py-3 space-y-1.5">
        <Row label={t('agent.emergencyForm.row.wallet')}  value={walletDisplay}                    valueClass="text-rose-700 dark:text-rose-300" />
        <Row label={t('agent.emergencyForm.row.deposit')} value={inrAbs(data.depositBalance)} />
      </div>

      {/* Phase 10 follow-up (2026-05-12): three-tier recovery now
          renders in BOTH 'warning' and 'locked' — the agent right at
          the 80% line is one bad settlement away from lockout, so
          they deserve the same three choices instead of a single
          "barely escape" number. Tone + headline copy flip on
          severity; the tier math + descriptions are identical. */}
      <div className={`rounded-2xl border-2 p-4 space-y-3 ${
        isLocked
          ? 'border-rose-200 dark:border-rose-500/40 bg-rose-50/60 dark:bg-rose-900/15'
          : 'border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-900/15'
      }`}>
        <div>
          <div className={`text-[11px] uppercase tracking-[0.18em] font-extrabold mb-1 ${
            isLocked ? 'text-rose-700 dark:text-rose-200' : 'text-amber-700 dark:text-amber-200'
          }`}>
            {t(isLocked ? 'agent.emergencyForm.tier.eyebrow.locked' : 'agent.emergencyForm.tier.eyebrow.warning')}
          </div>
          <p className={`text-[12.5px] leading-relaxed ${
            isLocked ? 'text-rose-900 dark:text-rose-100' : 'text-amber-900 dark:text-amber-100'
          }`}>
            {t(isLocked
              ? 'agent.emergencyForm.tier.context.locked'
              : 'agent.emergencyForm.tier.context.warning',
              { wallet: walletDisplay, deposit: inrAbs(data.depositBalance) })}
          </p>
        </div>

        <div className={`border-t pt-3 ${
          isLocked ? 'border-rose-200 dark:border-rose-500/30' : 'border-amber-200 dark:border-amber-500/30'
        }`}>
          <div className={`text-[11px] uppercase tracking-[0.18em] font-extrabold mb-2 ${
            isLocked ? 'text-rose-700 dark:text-rose-200' : 'text-amber-700 dark:text-amber-200'
          }`}>
            {t('agent.emergencyForm.tier.pickHowMuch')}
          </div>
          <div className="space-y-2">
            <TierOption
              label={t(isLocked ? 'agent.emergencyForm.tier.bareMin.label.locked' : 'agent.emergencyForm.tier.bareMin.label.warning')}
              amount={data.bareMinimum}
              tag={t('agent.emergencyForm.tier.bareMin.tag')}
              tagTone="rose"
              description={t(isLocked ? 'agent.emergencyForm.tier.bareMin.note.locked' : 'agent.emergencyForm.tier.bareMin.note.warning')}
              selected={Number(amount) === data.bareMinimum}
              onSelect={() => setAmount(String(data.bareMinimum))}
              severity={severity}
            />
            <TierOption
              label={t('agent.emergencyForm.tier.safeMin.label')}
              amount={data.safeMinimum}
              tag={t('agent.emergencyForm.tier.safeMin.tag')}
              tagTone="amber"
              description={t('agent.emergencyForm.tier.safeMin.note', {
                deposit: inrAbs(data.depositBalance),
                half: inrAbs(Math.ceil(0.5 * Math.abs(data.walletBalance))),
              })}
              selected={Number(amount) === data.safeMinimum}
              onSelect={() => setAmount(String(data.safeMinimum))}
              severity={severity}
            />
            <TierOption
              label={t('agent.emergencyForm.tier.full.label')}
              amount={data.recommended}
              tag={t('agent.emergencyForm.tier.full.tag')}
              tagTone="emerald"
              description={t('agent.emergencyForm.tier.full.note', {
                neg: inrAbs(Math.abs(data.walletBalance)),
              })}
              selected={Number(amount) === data.recommended}
              onSelect={() => setAmount(String(data.recommended))}
              severity={severity}
            />
          </div>
        </div>
      </div>

      {/* Parent payment instructions */}
      {parent ? (
        <div className="rounded-2xl bg-white dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/50 px-4 py-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-arena-600 dark:text-neon-200/80 mb-2">
            {t(parent.role === 'admin' ? 'agent.emergencyForm.parent.label.admin' : 'agent.emergencyForm.parent.label.parentAgent')}
          </div>
          <div className="flex items-start gap-4">
            {parent.qrImageUrl ? (
              <a href={parent.qrImageUrl} target="_blank" rel="noreferrer"
                 className="block shrink-0 w-32 h-32 rounded-xl overflow-hidden border border-arena-200/60 dark:border-arena-600/50 bg-white">
                <img src={parent.qrImageUrl} alt="Payment QR" className="w-full h-full object-contain" />
              </a>
            ) : (
              <div className="shrink-0 w-32 h-32 rounded-xl bg-arena-100/60 dark:bg-arena-700/40 grid place-items-center text-[11px] text-arena-600 dark:text-neon-200/70">
                {t('agent.recharge.parent.noQr')}
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-2">
              <div>
                <div className="text-[14px] font-bold text-arena-900 dark:text-white truncate">
                  {/* 2026-05-18 — Display name, never username. */}
                  <span className={(parent.display_name || '').trim() ? '' : 'opacity-60'}>
                    {(parent.display_name || '').trim() || '—'}
                  </span>
                  {parent.uaid ? ` · ${parent.uaid}` : ''}
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/80">
                  {t(parent.role === 'admin' ? 'agent.emergencyForm.parent.role.admin' : 'agent.emergencyForm.parent.role.parentAgent')}
                </div>
              </div>
              {parent.upiId && (
                <div className="text-[12px]">
                  <span className="text-arena-600 dark:text-neon-200/80 mr-1">{t('agent.emergencyForm.parent.upi')}</span>
                  <span className="font-mono text-arena-900 dark:text-white">{parent.upiId}</span>
                </div>
              )}
              {parent.whatsappNumber && (
                <a href={`https://wa.me/${parent.whatsappNumber.replace(/\D/g, '')}`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700 dark:text-emerald-300 hover:underline">
                  <Icon name="chat" size={13} />
                  {t('agent.emergencyForm.parent.whatsapp', { number: parent.whatsappNumber })}
                </a>
              )}
              {parent.supportPhone && parent.supportPhone !== parent.whatsappNumber && (
                <a href={`tel:${parent.supportPhone}`}
                   className="inline-flex items-center gap-1.5 text-[12px] text-arena-700 dark:text-neon-200/75 hover:underline">
                  <Icon name="call" size={12} />
                  {parent.supportPhone}
                </a>
              )}
              {parent.displayNote && (
                <p className="text-[11.5px] text-arena-700 dark:text-neon-200/70 italic leading-relaxed pt-1">
                  "{parent.displayNote}"
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/25 border border-amber-200 dark:border-amber-500/30 px-4 py-3 text-[12.5px] text-amber-800 dark:text-amber-200">
          {t('agent.emergencyForm.parent.notOnFile')}
        </div>
      )}

      {/* UTR submission form */}
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/80 mb-1">
            {t('agent.emergencyForm.field.amount.label')}
          </label>
          <input
            type="number"
            min={10}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full rounded-xl px-3 py-2 text-[14px] font-mono tabular-nums
                       bg-arena-50/40 dark:bg-arena-700/40
                       border border-arena-200/60 dark:border-arena-600/50
                       focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                       text-arena-900 dark:text-white"
            required
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/80 mb-1">
            {t('agent.emergencyForm.field.txn.label')}
          </label>
          <input
            type="text"
            value={txn}
            onChange={(e) => setTxn(e.target.value)}
            placeholder={t('agent.emergencyForm.field.txn.placeholder')}
            className="w-full rounded-xl px-3 py-2 text-[14px] font-mono
                       bg-arena-50/40 dark:bg-arena-700/40
                       border border-arena-200/60 dark:border-arena-600/50
                       focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                       text-arena-900 dark:text-white"
            required
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.14em] font-bold text-arena-600 dark:text-neon-200/80 mb-1">
            {t('agent.emergencyForm.field.note.label')}
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('agent.emergencyForm.field.note.placeholder')}
            className="w-full rounded-xl px-3 py-2 text-[14px]
                       bg-arena-50/40 dark:bg-arena-700/40
                       border border-arena-200/60 dark:border-arena-600/50
                       focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/30
                       text-arena-900 dark:text-white"
          />
        </div>
        {submitErr && (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-900/25 border border-rose-200 dark:border-rose-500/30 px-3 py-2 text-[12.5px] text-rose-700 dark:text-rose-200">
            {submitErr}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className={`w-full inline-flex items-center justify-center gap-1.5
                      rounded-full px-4 py-3 text-[14px] font-bold transition active:scale-95 disabled:opacity-60
                      ${tone === 'rose'
                        ? 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-[0_4px_14px_rgba(244,63,94,0.45)]'
                        : 'bg-amber-600 hover:bg-amber-500 active:bg-amber-700 text-white shadow-[0_4px_14px_rgba(245,158,11,0.45)]'}`}
        >
          {busy
            ? <Icon name="progress_activity" size={16} className="animate-spin" />
            : <Icon name="send" size={16} />}
          {busy ? t('agent.emergencyForm.ctaSubmitting') : t('agent.emergencyForm.cta')}
        </button>
      </form>
    </div>
  );
}

function Row({ label, value, valueClass = '' }: {
  label: string; value: string; valueClass?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12.5px] text-arena-700 dark:text-neon-200/75">{label}</span>
      <span className={`font-mono font-bold tabular-nums ${valueClass || 'text-arena-900 dark:text-white'}`}>
        {value}
      </span>
    </div>
  );
}

// Phase 9b v2 — one row of the three-tier recovery target chooser.
// Acts as a radio: clicking calls onSelect, the parent updates the
// amount field, and `selected` reflects whether the amount field
// currently matches this tier's value.
//
// Phase 10 follow-up (2026-05-12): TierOption now ALSO renders
// inside the warning banner, not just lockout. The `severity` prop
// flips the idle/unselected border color (rose for locked, amber
// for warning) so the picker visually agrees with its surrounding
// banner. Selected/tag colors stay per-tier so the agent's eye
// still maps "rose=risky, amber=recommended, emerald=best".
function TierOption({
  label, amount, tag, tagTone, description, selected, onSelect, severity,
}: {
  label: string;
  amount: number;
  tag: string;
  tagTone: 'rose' | 'amber' | 'emerald';
  description: string;
  selected: boolean;
  onSelect: () => void;
  severity: 'warning' | 'locked';
}) {
  const tagCls = {
    rose:    'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200',
    amber:   'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
  }[tagTone];
  const idleBorder = severity === 'locked'
    ? 'border-rose-200/70 dark:border-rose-500/25 bg-white dark:bg-arena-700/30 hover:border-rose-300 dark:hover:border-rose-400/40'
    : 'border-amber-200/70 dark:border-amber-500/25 bg-white dark:bg-arena-700/30 hover:border-amber-300 dark:hover:border-amber-400/40';
  const ringCls = selected
    ? (tagTone === 'rose'
        ? 'border-rose-400 dark:border-rose-400/60 bg-rose-100/40 dark:bg-rose-500/15 shadow-[0_2px_10px_rgba(244,63,94,0.25)]'
        : tagTone === 'amber'
          ? 'border-amber-400 dark:border-amber-400/60 bg-amber-100/40 dark:bg-amber-500/15 shadow-[0_2px_10px_rgba(245,158,11,0.25)]'
          : 'border-emerald-400 dark:border-emerald-400/60 bg-emerald-100/40 dark:bg-emerald-500/15 shadow-[0_2px_10px_rgba(16,185,129,0.25)]')
    : idleBorder;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full text-left rounded-xl border-2 px-3 py-2.5 transition active:scale-[0.99] ${ringCls}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {/* radio pip */}
          <span className={`grid place-items-center w-4 h-4 rounded-full border-2 shrink-0
                            ${selected
                              ? 'border-current bg-white dark:bg-arena-900'
                              : 'border-arena-300 dark:border-arena-600'}`}>
            {selected && (
              <span className="w-2 h-2 rounded-full bg-current"></span>
            )}
          </span>
          <span className="text-[13.5px] font-bold text-arena-900 dark:text-white">{label}</span>
          <span className={`text-[9.5px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded ${tagCls}`}>
            {tag}
          </span>
        </div>
        <span className="font-mono font-black tabular-nums text-[16px] text-arena-900 dark:text-white">
          ₹{amount.toLocaleString('en-IN')}
        </span>
      </div>
      <p className="text-[11px] text-arena-700 dark:text-neon-200/70 leading-snug mt-1.5">
        {description}
      </p>
    </button>
  );
}
